import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  AppWindow,
  Bell,
  Activity,
  BarChart3,
  Key,
  Settings,
  CreditCard,
  Users,
  ChevronLeft,
  ChevronRight,
  Book,
  Waves,
  ExternalLink,
} from 'lucide-react'
import { cn, formatNumber } from '@/lib/utils'
import { useAppStore } from '@/stores/appStore'
import { Button } from '@alertwhale/ui'
import { useBillingUsage, getUsagePercentage, getPlanLimit } from '@/hooks/useBillingUsage'

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Applications', href: '/applications', icon: AppWindow },
  { name: 'Notifications', href: '/notifications', icon: Bell },
  { name: 'Live Sessions', href: '/live-sessions', icon: Activity },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'API Keys', href: '/api-keys', icon: Key },
]

const externalLinks = [
  { name: 'API Docs', href: 'https://docs.alertwhale.com', icon: Book },
]

const settingsNavigation = [
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Team', href: '/team', icon: Users },
]

export function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useAppStore()
  const { data: usage } = useBillingUsage()
  const usagePercentage = getUsagePercentage(usage)
  const limit = getPlanLimit(usage?.plan || 'free')

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-opacity duration-300',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={toggleSidebar}
      />

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-out',
          'bg-slate-950/95 backdrop-blur-xl',
          'border-r border-slate-800/50',
          sidebarOpen ? 'w-64' : 'w-20',
          'translate-x-0 lg:translate-x-0',
          !sidebarOpen && 'max-lg:-translate-x-full'
        )}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-whale-500/[0.03] via-transparent to-transparent pointer-events-none" />

        {/* Logo */}
        <div className="relative flex h-16 items-center gap-3 px-4 border-b border-slate-800/50">
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-whale-500 to-whale-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-whale-500/20">
            <Waves className="w-5 h-5 text-white" />
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-xl bg-whale-500/20 blur-md -z-10" />
          </div>
          {sidebarOpen && (
            <div className="flex flex-col slide-in-left">
              <span className="font-bold text-lg tracking-tight">AlertWhale</span>
              <span className="text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                Command Center
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto py-6 px-3">
          <div className="space-y-1">
            {navigation.map((item, index) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    'hover:bg-white/[0.04]',
                    isActive
                      ? 'bg-whale-500/10 text-whale-400 nav-active-indicator'
                      : 'text-slate-400 hover:text-white'
                  )
                }
                style={{ animationDelay: `${index * 0.03}s` }}
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                    'group-hover:bg-white/[0.05]'
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                </div>
                {sidebarOpen && (
                  <span className="truncate">{item.name}</span>
                )}
                {/* Active indicator dot for collapsed state */}
                {!sidebarOpen && (
                  <div className="absolute left-1 w-1 h-1 rounded-full bg-primary opacity-0 group-[.nav-active-indicator]:opacity-100 transition-opacity" />
                )}
              </NavLink>
            ))}

            {/* External Links */}
            {externalLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  'hover:bg-white/[0.04]',
                  'text-slate-400 hover:text-white'
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                    'group-hover:bg-white/[0.05]'
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                </div>
                {sidebarOpen && (
                  <>
                    <span className="truncate">{item.name}</span>
                    <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                  </>
                )}
              </a>
            ))}
          </div>

          {/* Divider */}
          <div className="my-6 mx-3">
            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          </div>

          {/* Settings Navigation */}
          <div className="space-y-1">
            {sidebarOpen && (
              <div className="px-3 mb-2">
                <span className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                  Settings
                </span>
              </div>
            )}
            {settingsNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    'hover:bg-white/[0.04]',
                    isActive
                      ? 'bg-whale-500/10 text-whale-400 nav-active-indicator'
                      : 'text-slate-400 hover:text-white'
                  )
                }
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                    'group-hover:bg-white/[0.05]'
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                </div>
                {sidebarOpen && (
                  <span className="truncate">{item.name}</span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Bottom section with toggle */}
        <div className="relative p-3 border-t border-slate-800/50">
          {/* Usage indicator (shown when expanded) */}
          {sidebarOpen && (
            <div className="mb-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">API Usage</span>
                <span className={cn(
                  'text-xs font-medium',
                  usagePercentage >= 90 ? 'text-destructive' :
                  usagePercentage >= 70 ? 'text-warning' : 'text-primary'
                )}>{usagePercentage}%</span>
              </div>
              <div className="progress-glow">
                <div style={{ width: `${usagePercentage}%` }} />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-muted-foreground">
                  {formatNumber(usage?.notifications || 0)} / {formatNumber(limit)}
                </span>
                <span className="text-[10px] text-muted-foreground capitalize">
                  {usage?.plan || 'free'} plan
                </span>
              </div>
            </div>
          )}

          {/* Toggle button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn(
              'w-full h-10 flex items-center justify-center rounded-lg',
              'bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04]',
              'text-muted-foreground hover:text-foreground transition-all duration-200'
            )}
          >
            {sidebarOpen ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </div>
      </aside>
    </>
  )
}
